import { SortOrder } from "@lxc/app-device-common/src/types";
import { useFetchPageV2 } from "~/core/composables/useFetchPageV2";
import type { SearchMode } from "~/core/composables/useSearch";
import { useSearch } from "~/core/composables/useSearch";
import { useSort } from "~/core/composables/useSort";
import filtersUtils from "~/core/utils/filters.utils";
import { FirmwareColumns } from "~/modules/firmware/models/FirmwareColumns.enum";
import firmwareService from "~/modules/firmware/services/firmware.service";
import {
  Filters,
  type FiltersSelection,
  FiltersType,
  OPERATOR_GREATER_OR_EQUAL,
  OPERATOR_LOWER_OR_EQUAL,
  OPERATOR_RSQL_ARRAY_CONTAINS_JSONB,
  OPERATOR_RSQL_ILIKE,
  OPERATOR_RSQL_IN,
} from "~/types";

export function useFetchFirmwares({
  businessUnitCode,
  organizationCode,
  searchMode,
  useQueryParametersForPagination,
}: {
  businessUnitCode?: number;
  organizationCode?: number;
  searchMode?: SearchMode;
  useQueryParametersForPagination?: boolean;
}) {
  const service = organizationCode
    ? firmwareService.getOrganizationFirmwares.bind(firmwareService)
    : firmwareService.getBusinessUnitFirmwares.bind(firmwareService);

  const appliedFilters: FiltersSelection = new Map([
    [
      Filters.FIRMWARE_NAME_OR_VERSION,
      {
        key: ["name", "version"],
        operator: OPERATOR_RSQL_ILIKE,
        value: "",
        valueFormatter: filtersUtils.formatRSQLValue,
      },
    ],
    [
      Filters.STATUS,
      {
        key: "status",
        operator: OPERATOR_RSQL_IN,
        value: [],
        valueFormatter: filtersUtils.formatRSQLValue,
      },
    ],
    [
      Filters.RANGE_FRIENDLY_NAME,
      {
        key: "data.deviceTypeFriendlyName",
        operator: OPERATOR_RSQL_IN,
        value: [],
        valueFormatter: filtersUtils.formatRSQLValue,
      },
    ],
    [
      Filters.RANGE,
      {
        key: "data.deviceType",
        operator: OPERATOR_RSQL_IN,
        value: [],
        valueFormatter: filtersUtils.formatRSQLValue,
      },
    ],
    [
      Filters.MODEL_DECLINATION,
      {
        key: "data.deviceDeclination",
        operator: OPERATOR_RSQL_IN,
        value: [],
        valueFormatter: filtersUtils.formatRSQLValue,
      },
    ],
    [
      Filters.FIRMWARE_VERSIONS,
      {
        key: "data.compatibleGlobalProductSwVersion",
        operator: OPERATOR_RSQL_ARRAY_CONTAINS_JSONB,
        value: [],
        valueFormatter: filtersUtils.formatRSQLValue,
      },
    ],
    [
      Filters.HARDWARE_VERSIONS,
      {
        key: "data.compatibleHwVersion",
        operator: OPERATOR_RSQL_ARRAY_CONTAINS_JSONB,
        value: [],
        valueFormatter: filtersUtils.formatRSQLValue,
      },
    ],
    [
      Filters.DATE_ADDED_AFTER,
      { key: "createdAt", operator: OPERATOR_GREATER_OR_EQUAL, value: "" },
    ],
    [
      Filters.DATE_ADDED_BEFORE,
      { key: "createdAt", operator: OPERATOR_LOWER_OR_EQUAL, value: "" },
    ],
  ]);

  const { searchParams, filters, setFilter, search, onSearch } = useSearch(
    FiltersType.RSQL,
    appliedFilters,
    searchMode,
  );
  const { sort, onSortChange } = useSort({
    prop: FirmwareColumns.CREATION_DATE,
    order: SortOrder.DESC,
  });
  const { isLoading, results, error, fetchData } = useFetchPageV2({
    businessUnitCode,
    organizationCode,
    service,
    searchParams,
    sort,
    sortOverrideCallback: search,
    useQueryParametersForPagination,
  });

  return {
    isLoading,
    results,
    error,
    filters,
    fetchData,
    setFilter,
    onSortChange,
    search,
    onSearch,
  };
}
